import { z } from 'zod';

import { EntityId, HeroBaseFields, I18nText, SearchText, Tier, ToneName } from './primitives';

/** One audio rendition of a song (`song_tones`). */
export const ToneDTO = z.object({
  name: ToneName,
  label: I18nText,
  semitones: z.number().int().min(-12).max(12),
  isDefault: z.boolean(),
});
export type ToneDTO = z.infer<typeof ToneDTO>;

export const ArtistRef = z.object({ id: EntityId, name: z.string() });
export type ArtistRef = z.infer<typeof ArtistRef>;

export const ArtistDTO = z.object({
  id: EntityId,
  name: z.string(),
  imageUrl: z.string().nullable(),
  songCount: z.number().int().nonnegative().optional(),
});
export type ArtistDTO = z.infer<typeof ArtistDTO>;

/** `GET /songs/:id` and every song card. `locked` is computed per caller. */
export const SongDTO = z.object({
  id: EntityId,
  title: z.string(),
  artists: z.array(ArtistRef),
  imageUrl: z.string().nullable(),
  /** Normalized genre (WP0.9); null when neither the catalog nor the providers gave one. */
  genre: z.string().nullable(),
  tier: Tier,
  tones: z.array(ToneDTO).min(1),
  durationMs: z.number().int().nonnegative().nullable(),
  hasLyrics: z.boolean(),
  locked: z.boolean(),
  /** For `/cancion/{slug}` (docs/13). Absent on search entries cached before it existed. */
  slug: z.string().optional(),
});
export type SongDTO = z.infer<typeof SongDTO>;

export const PlaylistKind = z.enum(['editorial', 'artist']);
export type PlaylistKind = z.infer<typeof PlaylistKind>;

export const PlaylistDTO = z.object({
  id: EntityId,
  kind: PlaylistKind,
  name: I18nText,
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  artistId: EntityId.nullable(),
  total: z.number().int().nonnegative(),
});
export type PlaylistDTO = z.infer<typeof PlaylistDTO>;

const HeroBase = z.object(HeroBaseFields);

/** Home hero. Carries the target so Enter can open the player / playlist directly. */
export const HeroDTO = z.discriminatedUnion('type', [
  HeroBase.extend({ type: z.literal('song'), refId: EntityId, song: SongDTO }),
  HeroBase.extend({ type: z.literal('playlist'), refId: EntityId, playlist: PlaylistDTO }),
]);
export type HeroDTO = z.infer<typeof HeroDTO>;

export const HomeRowType = z.enum(['heroes', 'playlists', 'songs']);
export type HomeRowType = z.infer<typeof HomeRowType>;

const HomeRowBase = z.object({ id: EntityId, title: I18nText });

export const HomeRowDTO = z.discriminatedUnion('type', [
  HomeRowBase.extend({ type: z.literal('heroes'), items: z.array(HeroDTO) }),
  HomeRowBase.extend({ type: z.literal('playlists'), items: z.array(PlaylistDTO) }),
  HomeRowBase.extend({ type: z.literal('songs'), items: z.array(SongDTO) }),
]);
export type HomeRowDTO = z.infer<typeof HomeRowDTO>;

/** `GET /home` */
export const HomeResponse = z.object({ rows: z.array(HomeRowDTO) });
export type HomeResponse = z.infer<typeof HomeResponse>;

/** `GET /playlists/:id` — paged song list. */
export const PlaylistSongsQuery = z.object({
  offset: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type PlaylistSongsQuery = z.infer<typeof PlaylistSongsQuery>;
export type PlaylistSongsQueryInput = z.input<typeof PlaylistSongsQuery>;

export const PlaylistResponse = z.object({
  playlist: PlaylistDTO,
  songs: z.array(SongDTO),
  total: z.number().int().nonnegative(),
});
export type PlaylistResponse = z.infer<typeof PlaylistResponse>;

/** `GET /artists/:id` — same shape as a playlist (`kind: 'artist'`) plus the artist. */
export const ArtistResponse = PlaylistResponse.extend({ artist: ArtistDTO });
export type ArtistResponse = z.infer<typeof ArtistResponse>;

/** `GET /songs/:id` */
export const SongResponse = SongDTO;
export type SongResponse = z.infer<typeof SongResponse>;

/** `GET /songs/:id/play?tone=` */
export const PlayQuery = z.object({ tone: ToneName.optional() });
export type PlayQuery = z.infer<typeof PlayQuery>;

export const PlayDrm = z.object({ widevineLicenseUrl: z.string() });
export type PlayDrm = z.infer<typeof PlayDrm>;

export const PlayResponse = z.object({
  kind: z.enum(['mp4', 'hls']),
  /** Signed MP4 URL (M1) or signed HLS master (M4). */
  url: z.string(),
  /** Signed derived audio when kind='mp4', tone ≠ default and the asset exists. */
  toneUrl: z.string().optional(),
  drm: PlayDrm.optional(),
  tones: z.array(ToneDTO).min(1),
  /** The tone the caller asked for (the song's default tone when `tone=` was omitted). */
  toneRequested: ToneName,
  /**
   * The tone the returned URLs actually play. It differs from `toneRequested` when the
   * rendition is not available — there is no tone worker in this environment, so the API
   * falls back to the song's default tone (the source audio) instead of failing
   * (docs/07 §4, decision 2026-09-14). Clients play it and say so once; they never treat
   * the fallback as a playback error.
   */
  toneApplied: ToneName,
  /** `toneApplied === toneRequested`. Kept for clients that predate the fallback. */
  toneReady: z.boolean(),
  lrcUrl: z.string().optional(),
});
export type PlayResponse = z.infer<typeof PlayResponse>;

/**
 * How deep `GET /search?offset=` may page the songs (UX-047). 30 per page × 34 pages is far
 * past what anyone walks to with a D-pad, and it keeps `LIMIT … OFFSET …` off the deep end of
 * a 143 k-row FTS scan. A caller that needs more has to type a better query.
 */
export const SEARCH_MAX_OFFSET = 1000;

/** `GET /search?q=&offset=` — `offset` pages the **songs** only (UX-047). */
export const SearchQuery = z.object({
  q: SearchText,
  offset: z.coerce.number().int().nonnegative().max(SEARCH_MAX_OFFSET).default(0),
});
export type SearchQuery = z.infer<typeof SearchQuery>;
export type SearchQueryInput = z.input<typeof SearchQuery>;

export const SEARCH_LIMITS = { songs: 30, artists: 10, playlists: 10 } as const;

export const SearchResponse = z.object({
  songs: z.array(SongDTO).max(SEARCH_LIMITS.songs),
  artists: z.array(ArtistDTO).max(SEARCH_LIMITS.artists),
  playlists: z.array(PlaylistDTO).max(SEARCH_LIMITS.playlists),
  /**
   * Paging of the song results (UX-047). **Optional on purpose**: a client that talks to a
   * deploy older than UX-047 gets none of these fields back, and must then behave exactly as
   * it did before — one page, no "ver más". Never make them required.
   */
  songsOffset: z.number().int().nonnegative().optional(),
  /** True when songs past this page exist; the clients show their "load more" only then. */
  songsHasMore: z.boolean().optional(),
  /** The `offset` to ask for next; absent when `songsHasMore` is false. */
  songsNextOffset: z.number().int().nonnegative().optional(),
});
export type SearchResponse = z.infer<typeof SearchResponse>;
